import React, { useCallback, useEffect, useState } from "react";
import {
  Input,
  Form,
  AutoComplete,
} from "antd";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import GradientButton from "../../../components/common/UI/GradientButton";
import ENDPOINTS from "../../../services/endpoint";
import apiInstance from "../../../services/apiService";
import { storageService } from "../../../services/storageService";
import SettingsSidebarHeader from "../../../components/common/SettingsSidebarHeader";
import "./index.scss";

interface Emergency911Props {
  handleBackToPrevious: () => void;
}

const Emergency911: React.FC<Emergency911Props> = ({
  handleBackToPrevious,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<any>(null);
  const [locationDetails, setLocationDetails] = useState<any>(null);
  const uid = storageService.getUdid();

  const handleSubmit = async (values: any) => {
    const url = ENDPOINTS.EMERGENCY_911;
    setLoading(true);
    try {
      const response = await apiInstance.post(url, values);
      toast.success("E911 address updated successfully!");
      form.resetFields();
      setSearchValue("");
    } catch (error) {
      toast.error("Failed to update E911 address. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (value: string) => {
    const idAgainstValue = autoCompleteOptions.find((option: any) =>
      option.label === value ? option.id : null
    );

    if (!idAgainstValue) return;

    const url = ENDPOINTS.LOCATION_DETAIL;

    try {
      const response = await apiInstance.post(url, {
        placeId: idAgainstValue.id,
        sessiontoken: uid,
      });
      const data = response.data.data;
      setLocationDetails(data);
    } catch (error) {
      toast.error("Failed to update E911 address. Please try again.");
    }
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) return;

    const url = ENDPOINTS.LOCATION_SEARCH;

    try {
      const response = await apiInstance.post(url, {
        location: query,
        sessiontoken: uid,
      });
      const data = response.data.data.predictions;
      setAutoCompleteOptions(
        data.map((option: any) => ({
          id: option.place_id,
          label: option.description,
          value: option.description,
        }))
      );
    } catch (error: any) {
      toast.error(error.message || "There was an error fetching locations");
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => handleSearch(query), 500),
    []
  );

  useEffect(() => {
    if (searchValue.trim()) {
      debouncedSearch(searchValue);
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [searchValue, debouncedSearch]);

  useEffect(() => {
    if (locationDetails) {
      form.setFieldsValue({
        streetNumber: locationDetails.streetNumber || "",
        streetName: locationDetails.street || "",
        city: locationDetails.city || "",
        province: locationDetails.state || "",
        country: locationDetails.country || "",
        postalCode: locationDetails.zipCode || "",
      });
    }
  }, [locationDetails, form]);

  return (
    <div className="emergency-911-sider" >
      <SettingsSidebarHeader
        title="Emergency 911"
        handleBackClick={handleBackToPrevious}
      />

      <div className="form-container">
        <label className="autocomplete-label">Search Location</label>
        <AutoComplete
          className="autocomplete"
          value={searchValue}
          options={autoCompleteOptions}
          style={{ width: "100%" }}
          onChange={(e) => setSearchValue(e)}
          onSelect={(e) => handleSelect(e)}
          placeholder="Search location"
        />

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Street Number"
            name="streetNumber"
            className="street-number"
            rules={[{ required: true, message: "Street number is required" }]}
          >
            <Input placeholder="Enter street number" size="large" />
          </Form.Item>

          <Form.Item
            label="Street Name"
            name="streetName"
            className="street-name"
            rules={[{ required: true, message: "Street name is required" }]}
          >
            <Input placeholder="Enter street name" size="large" />
          </Form.Item>

          <div className="form-row">
            <Form.Item
              label="City"
              name="city"
              className="form-half"
              rules={[{ required: true, message: "City is required" }]}
            >
              <Input placeholder="Select city" size="large" />
            </Form.Item>

            <Form.Item
              label="Province"
              name="province"
              className="form-half"
              rules={[{ required: true, message: "Province is required" }]}
            >
              <Input placeholder="Select province" size="large" />
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item
              label="Country"
              name="country"
              className="form-half"
              rules={[{ required: true, message: "Country is required" }]}
            >
              <Input placeholder="Select country" size="large" />
            </Form.Item>

            <Form.Item
              label="Postal Code"
              name="postalCode"
              className="form-half"
              rules={[{ required: true, message: "Postal code is required" }]}
            >
              <Input placeholder="Enter postal code" size="large" />
            </Form.Item>
          </div>

          <Form.Item className="submit-btn">
            <GradientButton htmlType="submit" loading={loading}>
              Submit
            </GradientButton>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default Emergency911;
